import React from 'react'
import { Card, Descriptions, Empty, Space, Table, Tag, Typography } from 'antd'
import i from '../../../../translations/i'

export type ModifierRules = {
    id: number
    name: string
    enabled?: boolean | number
    required?: boolean | number
    hidden?: boolean | number
    min_count?: number
    max_count?: number
    default_count?: number
    free_count?: number
    price?: number
    restrictions_source?: string
    modifiers?: ModifierRules[]
}

const flag = (value: unknown) => value === true || value === 1 || value === '1'
const minimum = (item: ModifierRules) => Math.max(Number(item.min_count) || 0, flag(item.required) ? 1 : 0)
const maximum = (value?: number) => Number(value) > 0 ? Number(value) : i.t('modifiersGroup:unlimited')

export const ModifierRestrictions = ({ groups }: { groups: ModifierRules[] }) => {
    if (groups.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />

    const columns = [
        { title: i.t('def:name'), dataIndex: 'name', key: 'name' },
        {
            title: i.t('modifier:required'), key: 'required',
            render: (_: unknown, item: ModifierRules) => minimum(item) > 0 ? i.t('def:yes') : i.t('def:no'),
        },
        { title: i.t('modifiersGroup:minimum'), dataIndex: 'min_count', key: 'min_count', render: (_: unknown, item: ModifierRules) => minimum(item) },
        { title: i.t('modifiersGroup:maximum'), dataIndex: 'max_count', key: 'max_count', render: maximum },
        { title: i.t('modifier:defaultCount'), dataIndex: 'default_count', key: 'default_count', render: (value?: number) => Number(value) || 0 },
        { title: i.t('modifier:freeCount'), dataIndex: 'free_count', key: 'free_count', render: (value?: number) => Number(value) || 0 },
        { title: i.t('modifier:price'), dataIndex: 'price', key: 'price', render: (value?: number) => Number(value) || 0 },
        { title: i.t('def:active'), dataIndex: 'enabled', key: 'enabled', render: (value: unknown) => flag(value) ? i.t('def:yes') : i.t('def:no') },
        { title: i.t('modifier:hidden'), dataIndex: 'hidden', key: 'hidden', render: (value: unknown) => flag(value) ? i.t('def:yes') : i.t('def:no') },
    ]

    return <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {groups.map(group => <Card
            key={group.id}
            size="small"
            title={<Space wrap>
                <Typography.Link href={`/shop/modifiers_groups/${group.id}`} target="_blank" rel="noopener noreferrer">
                    {group.name} · #{group.id}
                </Typography.Link>
                <Tag color={group.restrictions_source === 'product' ? 'blue' : undefined}>
                    {i.t(group.restrictions_source === 'product' ? 'modifiersGroup:productRules' : 'modifiersGroup:sharedRules')}
                </Tag>
                {minimum(group) > 0 && <Tag color="orange">{i.t('modifiersGroup:requiredFem')}</Tag>}
                {!flag(group.enabled) && <Tag>{i.t('def:off')}</Tag>}
            </Space>}
        >
            <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
                <Descriptions.Item label={i.t('modifiersGroup:requiredFem')}>{minimum(group) > 0 ? i.t('def:yes') : i.t('def:no')}</Descriptions.Item>
                <Descriptions.Item label={i.t('modifiersGroup:minimum')}>{minimum(group)}</Descriptions.Item>
                <Descriptions.Item label={i.t('modifiersGroup:maximum')}>{maximum(group.max_count)}</Descriptions.Item>
                <Descriptions.Item label={i.t('modifiersGroup:hiddenFem')}>{flag(group.hidden) ? i.t('def:yes') : i.t('def:no')}</Descriptions.Item>
            </Descriptions>
            <Table<ModifierRules>
                size="small"
                rowKey="id"
                columns={columns}
                dataSource={group.modifiers ?? []}
                pagination={false}
                scroll={{ x: 1000 }}
            />
        </Card>)}
    </Space>
}
